import { IUser } from '@core-ports/outputs/user';
import { Observable, catchError, throwError } from 'rxjs';
import { Apollo } from 'apollo-angular';
import { IUserInput } from '@core-ports/inputs/user';
import { CREATE_USER, GET_USER, GET_USERS, GET_ADMIN_USERS, SEARCH_USERS_BY_MUNICIPALITY, UPDATE_USER, DELETE_USER } from '@infra-adapters/graphql/operation/user.operations';
import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { ErrorMessage } from '@core-interfaces/global';
import { LOGIN_USER, NEW_PASSWORD, RECOVER_PASSWORD } from '../graphql/operation/auth.operations';
import { IRecoverPasswordRequest } from '../services/auth.service';

export interface INewPasswordRequest {
  token: string;
  newPassword: string;
}

export interface IUserRepository {
  getUsers(): Observable<IUser[]>;
  searchUsersByMunicipality(municipalityId: number, search?: string, limit?: number): Observable<IUser[]>;
  getAdminUsers(): Observable<IUser[]>;
  getUserDetail(id: number): Observable<IUser>;
  createUser(input: IUserInput): Observable<IUser>;
  updateUser(id: number, input: Partial<IUserInput>): Observable<IUser>;
  deleteUser(id: number): Observable<boolean>;
  loginUser(credentials: ILoginCredentials): Observable<ILoginResponse>;
  recoverPassword(request: IRecoverPasswordRequest): Observable<boolean>;
  newPassword(request: INewPasswordRequest): Observable<boolean>;
}

export interface ILoginCredentials {
  email: string;
  password: string;
}

export interface ILoginResponse {
  id: number;
  token: string;
}

@Injectable({
  providedIn: 'root',
})
export class UserRepositoryImpl implements IUserRepository {
  readonly errorMessages: ErrorMessage = {
    user: {
      notFound: 'Usuario no encontrado',
      serverError: 'Error en el servidor'
    }
  };

  constructor(private apollo: Apollo) {}

  getUsers(): Observable<IUser[]> {
    return this.apollo
      .watchQuery<{ getUsers: IUser[] }>({
        query: GET_USERS,
        errorPolicy: 'all',
      })
      .valueChanges.pipe(
        map(result => {
          if (result.errors) {
            console.error('GraphQL errors:', result.errors);
            throw new Error(result.errors.map(e => e.message).join(', '));
          }
          return result.data.getUsers;
        }),
        catchError(error => {
          console.error('Error en getUsers:', error);
          return throwError(() => new Error('No se pudieron cargar los usuarios. ' + error.message));
        })
      );
  }

  searchUsersByMunicipality(municipalityId: number, search?: string, limit = 50): Observable<IUser[]> {
    return this.apollo
      .query<{ searchUsersByMunicipality: IUser[] }>({
        query: SEARCH_USERS_BY_MUNICIPALITY,
        variables: { municipality_id: municipalityId, search: search || null, limit },
        fetchPolicy: 'network-only',
      })
      .pipe(
        map((result) => {
          if (result.errors?.length) {
            console.error('GraphQL errors:', result.errors);
            throw new Error(result.errors.map((e) => e.message).join(', '));
          }
          return result.data?.searchUsersByMunicipality ?? [];
        }),
        catchError((error) => {
          console.error('Error en searchUsersByMunicipality:', error);
          return throwError(() => new Error('No se pudieron buscar usuarios. ' + error.message));
        })
      );
  }

  getAdminUsers(): Observable<IUser[]> {
    return this.apollo
      .watchQuery<{ getAdminUsers: IUser[] }>({
        query: GET_ADMIN_USERS,
        errorPolicy: 'all',
      })
      .valueChanges.pipe(
        map(result => {
          if (result.errors) {
            console.error('GraphQL errors:', result.errors);
            throw new Error(result.errors.map(e => e.message).join(', '));
          }
          return result.data.getAdminUsers;
        }),
        catchError(error => {
          console.error('Error en getAdminUsers:', error);
          return throwError(() => new Error('No se pudieron cargar los usuarios administradores. ' + error.message));
        })
      );
  }

  getUserDetail(id: number): Observable<IUser> {
    return this.apollo
      .watchQuery<{ getUser: IUser }>({
        query: GET_USER,
        variables: { id },
      })
      .valueChanges.pipe(
        map(result => {
          if (result.errors) {
            console.error('GraphQL errors:', result.errors);
            throw new Error(result.errors.map(e => e.message).join(', '));
          }
          return result.data.getUser;
        }),
        catchError(error => {
          console.error('Error en getUserDetail:', error);
          return throwError(() => new Error('No se pudo cargar el detalle del usuario. ' + error.message));
        })
      );
  }

  createUser(input: IUserInput): Observable<IUser> {
    return this.apollo
      .mutate<{ createUser: IUser }>({
        mutation: CREATE_USER,
        variables: { ...input },
        refetchQueries: [{ query: GET_USERS }],
      })
      .pipe(
        map(result => {
          if (!result.data) {
            throw new Error('No se recibieron datos al crear el usuario');
          }
          return result.data.createUser;
        }),
        catchError(error => {
          console.error('Error en createUser:', error);
          return throwError(() => new Error('No se pudo crear el usuario. ' + error.message));
        })
      );
  }

  updateUser(id: number, input: Partial<IUserInput>): Observable<IUser> {
    return this.apollo
      .mutate<{ updateUser: IUser }>({
        mutation: UPDATE_USER,
        variables: { user_id: id, ...input },
        refetchQueries: [{ query: GET_USERS }],
      })
      .pipe(
        map(result => {
          if (!result.data) {
            throw new Error('No se recibieron datos al actualizar el usuario');
          }
          return result.data.updateUser;
        }),
        catchError(error => {
          console.error('Error en updateUser:', error);
          return throwError(() => new Error('No se pudo actualizar el usuario. ' + error.message));
        })
      );
  }

  deleteUser(id: number): Observable<boolean> {
    return this.apollo
      .mutate<{ deleteUser: boolean }>({
        mutation: DELETE_USER,
        variables: { user_id: id },
        refetchQueries: [{ query: GET_USERS }],
      })
      .pipe(
        map(result => {
          if (result.data === undefined || result.data === null) {
            throw new Error('No se recibieron datos al eliminar el usuario');
          }
          if (result.errors && result.errors.length > 0) {
            throw new Error(result.errors[0].message);
          }
          return result.data!.deleteUser;
        }),
        catchError(error => {
          console.error('Error en deleteUser:', error);
          const errorMessage = error?.graphQLErrors?.[0]?.message || error?.message || 'Error desconocido';
          return throwError(() => new Error('No se pudo eliminar el usuario: ' + errorMessage));
        })
      );
  }

  loginUser(credentials: ILoginCredentials): Observable<ILoginResponse> {
    return this.apollo
      .mutate<{ loginUser: ILoginResponse }>({
        mutation: LOGIN_USER,
        variables: {
          email: credentials.email,
          password: credentials.password,
        },
      })
      .pipe(map((result) => result.data?.loginUser as ILoginResponse));
  }

  recoverPassword(request: IRecoverPasswordRequest): Observable<boolean> {
    const source = request.source || 'web';
    
    return this.apollo
      .mutate<{ recoverPassword: boolean }>({
        mutation: RECOVER_PASSWORD,
        variables: {
          email: request.email,
          source: source,
        },
      })
      .pipe(
        map(result => {
          if (result.data === undefined) {
            throw new Error('No se recibió respuesta del servidor');
          }
          return result.data!.recoverPassword;
        }),
        catchError(error => {
          console.error('Error en recoverPassword:', error);
          return throwError(() => new Error('No se pudo procesar la recuperación de contraseña. ' + error.message));
        })
      );
  }

  newPassword(request: INewPasswordRequest): Observable<boolean> {
    return this.apollo
      .mutate<{ newPassword: boolean }>({
        mutation: NEW_PASSWORD,
        variables: {
          token: request.token,
          newPassword: request.newPassword,
        },
      })
      .pipe(
        map(result => {
          if (result.data === undefined) {
            throw new Error('No se recibió respuesta del servidor');
          }
          return result.data!.newPassword;
        }),
        catchError(error => {
          console.error('Error en newPassword:', error);
          return throwError(() => new Error('No se pudo establecer la nueva contraseña. ' + error.message));
        })
      );
  }

  getErrorMessage(code: string): string {
    const [category, errorKey] = code.split('.');
    if (!category || !errorKey || !this.errorMessages[category]) {
      return 'Error desconocido';
    }
    return this.errorMessages[category][errorKey] || 'Error desconocido';
  }
}